/**
 * Hooks para mutaciones relacionadas con las preferencias del usuario.
 * Proporciona funciones para actualizar las preferencias usando TanStack Query.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

/**
 * Hook de mutación para actualizar las preferencias del usuario.
 * Usa TanStack Query para gestionar estado y invalidación de caché.
 * 
 * @returns {Object} - Objeto con mutate, isPending, error, etc.
 */
export const useUpdatePreferencesMutation = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;

  return useMutation({
    mutationFn: async (preferencesData) => {
      console.log('[useUpdatePreferencesMutation] Iniciando actualización de preferencias:', preferencesData);
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }

      try {
        // Verificar si ya existe un registro de preferencias
        const { data: existingPrefs, error: checkError } = await supabase
          .from('preferencias')
          .select('*')
          .eq('user_ref_id', userId)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          throw checkError;
        }

        let result;
        if (existingPrefs) {
          // Actualizar registro existente
          const { data, error } = await supabase
            .from('preferencias')
            .update({
              ...preferencesData,
              updated_at: new Date().toISOString()
            })
            .eq('user_ref_id', userId)
            .select()
            .single();

          if (error) throw error;
          result = data;
        } else {
          // Crear nuevo registro
          const { data, error } = await supabase
            .from('preferencias')
            .insert({
              user_ref_id: userId,
              ...preferencesData,
              updated_at: new Date().toISOString()
            })
            .select()
            .single();

          if (error) throw error;
          result = data;
        }

        console.log('[useUpdatePreferencesMutation] Preferencias actualizadas exitosamente:', result);
        return result;
      } catch (err) {
        console.error('[useUpdatePreferencesMutation] Error actualizando preferencias:', err);
        throw err;
      }
    },
    onSuccess: (data) => {
      console.log('[useUpdatePreferencesMutation] onSuccess - data:', data);
      
      // Invalidar queries relacionadas con preferencias
      queryClient.invalidateQueries(['userPreferences', userId]);
      
      // Invalidar query de usuarios cercanos para aplicar nuevos filtros
      queryClient.invalidateQueries(['nearbyUsers', userId]);
    },
    onError: (error) => {
      console.error('[useUpdatePreferencesMutation] onError - error:', error);
    },
    onMutate: () => {
      console.log('[useUpdatePreferencesMutation] onMutate - Iniciando mutación');
    },
    onSettled: () => {
      console.log('[useUpdatePreferencesMutation] onSettled - Mutación finalizada');
    },
    enabled: !!userId,
  });
};
